/**
 * Signal-Konzept Schritt 1 (06.08.2026, erweitert 07.08.2026) — die Zustandsmaschine.
 *
 * Sie ersetzt die Frage „Wie viele Punkte hat der Wert?" durch „In welchem
 * Zustand ist er, und bei welchem PREIS passiert was?".
 *
 * Warum eine Zustandsmaschine und keine bessere Zahl (Score-Audit + Konzept):
 * Ein Score ist eine SUMME, eine Kaufentscheidung eine UND-Verknüpfung. Hinter
 * „74" kann Qualität 95 / Timing 40 stehen — oder 60 / 90. Nur das zweite ist
 * ein Kauf. Jede Schwelle auf einer kompensierbaren Summe ist deshalb
 * zwangsläufig willkürlich. Der empirische Beleg: Das stärkste gefundene
 * Merkmal — Volatilitäts-Kontraktion — hat einen VORZEICHENWECHSEL. Am Hoch
 * senkt es die Trefferquote auf 24 % (schlechteste Zone im ganzen Datensatz),
 * im Drawdown hebt es sie auf 50 % und halbiert die Stop-Quote. Eine
 * Punkte-Summe kann eine Wechselwirkung mit Vorzeichenwechsel konstruktiv
 * nicht abbilden.
 *
 * DREI AUSLÖSER, EIN ZUSTAND (07.08.2026, erweitert 08.08.2026):
 *
 * ACHTUNG — die Trefferquoten in diesem Abschnitt sind WIDERLEGT. Sie
 * stammen aus dem Auswahlzeitraum und messen gegen ein +10-%/−8-%-Ziel,
 * das §7.4 selbst als Konstruktionsfehler verwirft. Die vorregistrierte
 * Neumessung (scripts/messung-kandidaten.ts) findet für KEINEN Auslöser
 * einen Vorteil gegenüber blindem Kaufen — weder in der Entwicklung noch
 * im gesperrten Zeitraum. Die Zahlen stehen nur noch als Zeitdokument da.
 * Belastbar ist einzig der Ausstieg (siehe zone-signals.ts).
 * Die Kaufzone öffnet über zwei unabhängig gemessene Wege. Sie überschneiden
 * sich nur zu ~13 % — es sind zwei verschiedene Bodentypen:
 *
 *   Auslöser „wende"   — 12-40 % unter dem 52W-Hoch + MA50 frisch von unten
 *                        zurückerobert + ATR14 < 80 % des ATR100
 *                        → 57,2 % Ziel / 25,1 % Stop, 339 Episoden, 15,4/Jahr
 *   Auslöser „squeeze" — 12-40 % unter dem 52W-Hoch + Bollinger-Bandbreite
 *                        unter 45 % ihres eigenen Jahres-Normalwerts +
 *                        20-Tage-Rendite nicht schlechter als der Markt
 *                        → 58,9 % Ziel / 31,5 % Stop, 270 Episoden, 12,3/Jahr
 *                        In-sample 0,59 ØR → out-of-sample 0,70 ØR (t = 3,59)
 *
 * Basisrate zum Vergleich: 39,8 % Ziel / 33,7 % Stop (11.888 Episoden,
 * 55 Werte, 2005-2026, Suche auf 2005-2015, Test auf 2016-2026).
 *
 * Beide erfüllen alle fünf Aufnahme-Kriterien aus §5b des Signal-Konzepts.
 * Ausdrücklich NICHT aufgenommen, weil durchgefallen: Vorjahrestief (ØR 0,07 —
 * die schlechteste geprüfte Idee), runde Zahlen, Fibonacci, Wochen-Hammer,
 * Wochen-Engulfing, MA30W-Rückeroberung, Volumen-Trockenheit, Markt-Regime als
 * Filter (SPY über/unter MA200: exakt null Effekt), Wochen-ATR-Kontraktion
 * (in-sample 0,57 → out-of-sample 0,02, klassischer Overfit).
 *
 * DIE REGEL, DIE DEN GANZEN UMBAU TRÄGT:
 * **Ein Zustand darf nie aus einer Score-Schwelle folgen.** Sobald irgendwo
 * `if (score >= X) → 'zone_offen'` steht, ist der alte Fehler zurück, nur unter
 * neuem Namen. Dieses Modul liest deshalb ausschließlich Preis und Struktur —
 * es importiert bewusst nichts aus der Score-Engine.
 *
 * Reine Funktion, keine Seiteneffekte, keine Netzwerkzugriffe → über die
 * vorhandene Bar-Historie vollständig rückrechenbar. Das ist der Grund, warum
 * dieser Signaltyp als einziger im System ehrlich messbar ist: Der bisherige
 * Score hängt an Finnhub-Feldern, deren historische Werte nirgends gespeichert
 * sind — ein Backtest wäre zwangsläufig Look-ahead.
 */

import type { OhlcvBar } from '@/lib/market-data/types';

// =====================================================================
// SCHWELLEN — zentral und benannt. Herkunft jeweils dokumentiert, damit
// später nachvollziehbar ist, was gemessen und was gesetzt wurde.
// =====================================================================

/** Ab hier gilt ein Wert als antizyklischer Kandidat (Drawdown vom 52W-Hoch). */
export const KANDIDAT_DD_MIN_PCT = -12;
/**
 * Tiefer als das ist kein Boden mehr, sondern ein fallendes Messer: Unterhalb
 * −40 % kippt die Stop-Quote in der Messung deutlich (Bärenmarkt-Zone).
 * Nachgemessen 07.08.: −40…−50 % ergibt out-of-sample ØR 0,07, ab −50 % negativ.
 */
export const KANDIDAT_DD_MAX_PCT = -40;

/**
 * Volatilitäts-Kontraktion: ATR14 im Verhältnis zum ATR100. Unter 0,80 heißt
 * „die Schwankung ist eingetrocknet". Sensitivität ist monoton (0,70 → 0,90),
 * kein Knife-Edge — das spricht gegen Kurvenanpassung. Eine ENGERE Schwelle
 * ist nachweislich Überanpassung: 0,70 ergibt in-sample ØR 0,73, out-of-sample
 * nur noch 0,31. 0,80 ist die richtige Schwelle, nicht die bequeme.
 */
export const KONTRAKTION_MAX = 0.80;

/**
 * Bollinger-Squeeze: heutige Bandbreite im Verhältnis zu ihrem eigenen
 * Jahres-Median. Unter 0,45 heißt „die Bänder sind auf weniger als die Hälfte
 * des Normalzustands zusammengelaufen".
 *
 * Wichtig: Der Squeeze wirkt AUSSCHLIESSLICH im Drawdown. Am 52W-Hoch gemessen
 * bringt er exakt nichts (34,3 % Ziel gegen 34,6 % ohne). Deshalb steht er
 * hinter der Kandidatur-Prüfung, nicht davor.
 */
export const SQUEEZE_MAX = 0.45;

/**
 * Relative Stärke über 20 Tage gegen den Markt, in Prozentpunkten.
 * ≥ 0 heißt „hat sich zuletzt nicht schlechter gehalten als der S&P 500".
 *
 * Nur das 20-Tage-Fenster trägt: über 63 Tage bricht es out-of-sample weg
 * (0,62 → 0,29). Und NUR die Rendite trägt — der naheliegende Vergleich
 * „fällt weniger als der Markt" (Drawdown gegen Drawdown) zeigt exakt null
 * Unterschied (0,29 gegen 0,29). Deshalb Rendite, nicht Drawdown.
 */
export const RS20_MIN_PP = 0;

/**
 * Mindestalter des 52-Wochen-Tiefs in Handelstagen — NUR für den
 * Squeeze-Auslöser. Ein Boden braucht vier bis neun Monate.
 *
 * Warum nur dort (eigene Rückrechnung 07.08.2026, getrennt nach Auslöser und
 * nach Zeitraum — genau die Prüfung, die in früheren Versionen gefehlt hat):
 *
 *   squeeze, Tief < 90 T:  Suche 48,1 % Ziel / 48,1 % Stop · Test 37,8 % / 45,9 %
 *   squeeze, Tief ≥ 90 T:  Suche 62,6 % / 28,3 %          · Test 65,4 % / 25,6 %
 *   wende,   Tief < 90 T:  Suche 51,9 % / 25,3 %          · Test 60,7 % / 21,3 %
 *   wende,   Tief ≥ 90 T:  Suche 61,5 % / 20,8 %          · Test 47,7 % / 32,3 %
 *
 * Beim Squeeze wirkt der Filter in BEIDEN Zeiträumen gleichgerichtet und stark
 * (Stop-Quote 46-48 % gegen 26-28 %). Beim Wende-Auslöser DREHT er sich
 * out-of-sample um — dort würde die Sperre gerade das abschneiden, was den
 * Auslöser ausmacht: Die frische MA50-Rückeroberung kommt naturgemäß wenige
 * Wochen nach dem Tief, nicht erst nach einem halben Jahr.
 *
 * Eine externe Untersuchung hatte den Filter pauschal für beide empfohlen. Das
 * wäre falsch gewesen — sie hatte ihn nur auf der Squeeze-Familie gemessen.
 */
export const SQUEEZE_TIEF_MIN_ALTER_BARS = 90;

/** Verbot 1: zu nah am Hoch. Allein gemessen 32,5 % Treffer — schlechter als Zufall. */
export const VERBOT_NAHE_HOCH_PCT = -5;
/**
 * Verbot 2: V-Rebound — ABGESCHAFFT am 08.08.2026.
 *
 * Die Begründung lautete: „>25 % unter Hoch + frisches Tief + ≥ +7 % in 5
 * Tagen ergab 44,3 % Treffer bei 54,5 % Stop." Diese Zahl stammt aus dem
 * Auswahlzeitraum. Neu gemessen auf 19.478 Einstiegen über 55 Jahre
 * (scripts/messung-zonensignale.ts) ist der Effekt NICHT VORHANDEN:
 *
 *   Erwartung  −0,051 R   95 %-Bereich [−0,194 , +0,080]
 *   Gewinner   +1,0 pp    95 %-Bereich [−1,7 , +4,1]
 *
 * Beide Bereiche enthalten die Null. Das Verbot hat in keiner Richtung eine
 * messbare Wirkung — es hat nur gesperrt.
 *
 * Der konkrete Schaden ist belegt: PYPL am 15.07.2026. Der Kurs brach mit
 * +16,5 % die MA200 von unten durch — der Stage-1→2-Übergang, auf den der
 * Nutzer wochenlang gewartet hatte. Die Zustandsmaschine wechselte an genau
 * diesem Tag von `boden_reift` auf `kein_setup` und nannte es „senkrechter
 * Rückschnapper". Der Wert lief von 55,52 auf 59,78.
 *
 * Ein Verbot ohne nachgewiesene Wirkung, das nachweislich gute Einstiege
 * blockiert, ist kein Schutz, sondern eine Fehlfunktion.
 *
 * Die Konstante bleibt als KENNZAHL erhalten — roc5 wird weiter berechnet und
 * angezeigt, damit ein senkrechter Anstieg sichtbar ist. Er sperrt nur nicht
 * mehr.
 */
export const ROC5_HINWEIS_PCT = 7;

/**
 * Der Satz, der an jedem Signal aus der tiefen Zone hängt. Bewusst wörtlich
 * und nicht abgekürzt: Wer hier kauft, soll wissen, dass die Zahl dahinter
 * die unsicherste im ganzen System ist.
 */
export const TIEFE_ZONE_WARNUNG =
  'ACHTUNG tiefe Zone: über 40 % unter dem Hoch. In unseren Daten ist das die beste Gruppe — ' +
  'aber dort fehlen alle Werte, die pleitegegangen sind. Die Zahl ist zu optimistisch, ' +
  'das Risiko begrenzt allein der Stop.';

/** Wie lange eine geöffnete Kaufzone gültig bleibt (Handelstage). */
export const ZONE_GUELTIG_BARS = 15;

/**
 * RÜCKEROBERUNG statt „steht darüber" — der entscheidende Unterschied.
 *
 * Erste Rückrechnung (06.08.) mit der schwächeren Bedingung „Kurs > MA50":
 * 49,6 % Ziel bei 35,9 % Stop — die Stop-Quote lag damit ÜBER der Basisrate
 * von 31,7 %. Gemessen war aber „MA50 von unten zurückerobert" (59 % / 23 %).
 * Der Unterschied ist nicht kosmetisch: „steht über der MA50" trifft auch
 * Werte, die seit Monaten darüber laufen — also gerade keine Wende, sondern
 * einen bereits gelaufenen Trend.
 *
 * Rückeroberung heißt: innerhalb der letzten `RECLAIM_FENSTER_BARS` lag der
 * Kurs noch UNTER der MA50 und liegt jetzt darüber.
 */
export const RECLAIM_FENSTER_BARS = 10;

/**
 * Stop-Abstand als Vielfaches des Tages-ATR14.
 *
 * WARUM 4,5 UND NICHT 3 (Auflösung eines Agenten-Widerspruchs, 07.08.2026):
 * Zwei unabhängige Untersuchungen kamen scheinbar zu gegensätzlichen Schlüssen.
 * Die eine empfahl 3 × ATR14 („der App-Stop ist zu weit"), die andere 2 ×
 * Wochen-ATR ≈ 6 × ATR14 („kein Stop unter 1,4 × Wochen-ATR"). Nachgerechnet
 * messen beide dasselbe und widersprechen sich nicht:
 *
 *   Stop-Weite     Ergebnis in %   Ergebnis in R   Stop-Quote
 *   3,0 × ATR14        +11,9 %         +1,60          44,8 %
 *   4,5 × ATR14        +14,0 %         +1,25          ~31 %
 *   6,0 × ATR14        +14,7 %         +0,98          23,4 %
 *
 * In Prozent ist weiter besser, in R ist enger besser — der Unterschied hängt
 * allein an der Positionsgröße. Der erste Agent maß in R gegen ein festes
 * 2R-Ziel; genau diese Konstruktion (festes Kursziel) hat der zweite Agent als
 * den zweitteuersten Konstruktionsfehler nachgewiesen. Sein „zu weit" ist also
 * ein Artefakt der Messmethode, nicht ein Befund über den Stop.
 *
 * Entscheidend ist: Über den Bereich 4,2 bis 10 × ATR14 liegt das Ergebnis in
 * einem monotonen Plateau zwischen +13,9 % und +16,9 % — es gibt kein scharfes
 * Optimum, das man treffen müsste. 4,5 liegt am unteren Ende dieses Plateaus
 * und ist damit der engste Stop, der noch nicht in die 45-%-Stop-Quote läuft.
 * Das entspricht dem, was die Zustandsmaschine seit dem 06.08. faktisch tut
 * (2 × ATR14 × √5 = 4,47) — nur ohne die irreführende „Wochen-ATR"-Benennung:
 * Der echte Wochen-ATR ist empirisch das 2,97-Fache des Tages-ATR, nicht das
 * √5 = 2,24-Fache. Die alte Konstante hat also nie das gemessen, was ihr Name
 * behauptete.
 *
 * Der Stop wird beim Einstieg gesetzt und NIE verändert. Nachziehen ist
 * durchgehend gemessen worden und durchgehend schlechter: Chandelier 4× kostet
 * 0,3 pp, Chandelier 2× kostet 5,3 pp, „unter das letzte Wochentief" reduziert
 * das Ergebnis auf ein Fünftel, und „halten bis die MA50 bricht" liefert 39,2 %
 * Gewinner — schlechter als Münzwurf.
 */
export const STOP_ATR14_FAKTOR = 4.5;

/** @deprecated Nur noch für Altcode. Ersetzt durch {@link STOP_ATR14_FAKTOR}. */
export const STOP_WOCHEN_ATR_FAKTOR = 2;

/** Welcher der drei Wege die Kaufzone geöffnet hat. Mehrere zugleich möglich. */
export type SetupAusloeser =
  | 'wende'
  | 'squeeze'
  | 'stufe2'
  | 'wende+squeeze'
  | 'wende+stufe2'
  | 'squeeze+stufe2'
  | 'wende+squeeze+stufe2';

/**
 * Wie viele Tage der Kurs vor dem Stufe-2-Durchbruch unter der MA200 gelegen
 * haben muss. 20 Handelstage = ein Monat: kurz genug, um den Durchbruch nicht
 * zu verpassen, lang genug, um tägliches Hin und Her um die Linie auszusieben.
 */
export const STUFE2_VORLAUF_BARS = 20;

// =====================================================================
// REGEL-IDENTITÄT — Produktversion ist nicht Regelversion (11.08.2026).
//
// Ronni: „Produkt: V6.0, Signalregel: Daily Reversal v1.2, ... Diese
// Versionen dürfen nicht mehr vermischt als V5.1 im UI erscheinen."
//
// Versionsgeschichte der Regel:
//   1.0  07.08.2026  Kandidatenfenster −12..−40 %, Auslöser wende + squeeze,
//                    Ausstieg A0 (fester Stop, 130-Bar-Uhr)
//   1.1  08.08.2026  Ausstieg A1 (halb bei +2R, Rest 15 % unter Lauf-Hoch),
//                    stufe2-Auslöser dazu, V-Rebound-Verbot gestrichen
//   1.2  10.08.2026  tiefe Zone (< −40 %) vom Verbot zur Warnung
//
// Der KONSTANTEN_HASH macht stille Schwellenänderungen sichtbar: Ändert
// jemand eine Schwelle, ohne REGEL_VERSION zu erhöhen, schlägt der
// Regressionstest an (setup-state-version.test.ts). Signale und Snapshots
// tragen damit eine prüfbare Regel-Identität.
// =====================================================================

export const REGEL_ID = 'daily_reversal';
export const REGEL_LABEL = 'Daily Reversal';
export const REGEL_VERSION = '1.2';

/** FNV-1a über die entscheidenden Schwellen — keine Abhängigkeiten, stabil. */
function fnv1a(text: string): string {
  let h = 0x811c9dc5;
  for (let i = 0; i < text.length; i++) {
    h ^= text.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return (h >>> 0).toString(16).padStart(8, '0');
}

export const KONSTANTEN_HASH = fnv1a(
  JSON.stringify([
    KANDIDAT_DD_MIN_PCT,
    KANDIDAT_DD_MAX_PCT,
    KONTRAKTION_MAX,
    SQUEEZE_MAX,
    RS20_MIN_PP,
    SQUEEZE_TIEF_MIN_ALTER_BARS,
    VERBOT_NAHE_HOCH_PCT,
    RECLAIM_FENSTER_BARS,
    STOP_ATR14_FAKTOR,
    STUFE2_VORLAUF_BARS,
  ])
);

/**
 * DAS Bar-Fenster der Regel — eine Konstante für alle Aufrufstellen.
 *
 * P0-Befund 11.08.2026: Die Watchlist lud 300 Bars, Symbolseite und Cron 420.
 * `atr()` ist eine Wilder-Rekursion über das GANZE übergebene Array; beim
 * ATR100 trägt der Startwert nach 300 Bars noch 13,5 % Gewicht, nach 420 nur
 * 4,1 %. Der Quotient ATR14/ATR100 entscheidet an KONTRAKTION_MAX über die
 * Kandidatur — ein Wert nahe der Schwelle war auf der Watchlist Kandidat und
 * auf der Symbolseite nicht.
 *
 * Zusätzlich schneidet `computeSetupState` selbst auf dieses Fenster zu
 * (bars.slice(-SETUP_BARS)): Wer MEHR übergibt, bekommt garantiert dasselbe
 * Ergebnis wie mit exakt 420. Die Messskripte übergeben Fenster derselben
 * Länge; damit rechnen Produktion, Anzeige und Messung dasselbe Modell.
 */
export const SETUP_BARS = 420;

/**
 * Ab wann sind die Kursdaten zu alt, um daraus einen HANDELBAREN Zustand
 * abzuleiten? Kalendertage zwischen letzter Bar und Bewertungstag.
 *
 * 5 deckt ein langes Wochenende mit Feiertag ab (Freitag-Bar, Dienstag
 * angesehen = 4 Tage). Darüber stimmt etwas nicht: fehlender Cron-Lauf,
 * eingefrorener Provider-Cache, ausgesetzter Handel.
 *
 * P0-3 aus dem Audit vom 11.08.2026: Vorher prüfte die Maschine nur die
 * ANZAHL der Bars (>= 252), nie ihr ALTER. Ein drei Wochen alter Cache
 * erzeugte ein selbstbewusstes `zone_offen` mit Limit und Stop. Der Zustand
 * bleibt weiterhin sichtbar — nur `tradeable` fällt auf false, mit Grund.
 */
export const MAX_DATENALTER_TAGE = 5;

/** Warum ein an sich offener Zustand NICHT handelbar ist. Maschinenlesbar. */
export type Blockiergrund = 'daten_veraltet' | 'daten_unvollstaendig';

export type SetupZustand =
  | 'kein_setup'
  | 'boden_reift'
  | 'zone_offen'
  | 'zone_verlassen'
  | 'ungueltig'
  | 'daten_unzureichend';

export interface SetupState {
  zustand: SetupZustand;
  /** Ein Satz Klartext — das, was in der Watchlist-Zeile steht. */
  text: string;
  /** Welcher Auslöser gefeuert hat. Nur bei `zone_offen` gesetzt. */
  ausloeser: SetupAusloeser | null;
  /** Obergrenze der Kaufzone (Limit). Nur bei `zone_offen` gesetzt. */
  limit_bis: number | null;
  /** Preis, unter dem die These widerlegt ist (= Stop). */
  ungueltig_unter: number | null;
  /** Preis, bei dem die Zone öffnen würde. Nur bei `boden_reift`. */
  trigger_ab: number | null;
  /** Wie viele Handelstage die Zone schon offen ist. */
  tag_von: number | null;
  /** Diagnose: welche Bedingung fehlt noch? Nie stillschweigend leer. */
  gruende: string[];
  /** Datum der letzten ausgewerteten Bar (`YYYY-MM-DD`). */
  as_of: string | null;
  /** Kalendertage zwischen letzter Bar und Bewertungstag. null = kein Tag übergeben. */
  datenalter_tage: number | null;
  /**
   * DAS Feld, das einen Kauf-Alert oder einen übernehmbaren Plan zulässt.
   * Genau dann true, wenn die Zone offen ist UND nichts blockiert.
   */
  tradeable: boolean;
  /** Leer, wenn nichts blockiert. Sonst maschinenlesbare Gründe. */
  blockiert_durch: Blockiergrund[];
  /** Für die spätere Messung: alle Kennzahlen zum Zeitpunkt der Bewertung. */
  kennzahlen: {
    dd52_pct: number | null;
    atr_quotient: number | null;
    ueber_ma50: boolean | null;
    roc5_pct: number | null;
    /** Tages-ATR14 in Kurseinheiten — Grundlage jeder Stop-Rechnung. */
    atr14: number | null;
    /** Bollinger-Bandbreite relativ zum eigenen Jahres-Median. */
    squeeze: number | null;
    /** 20-Tage-Rendite minus Markt-Rendite, in Prozentpunkten. */
    rs20_pp: number | null;
    /** Wie viele Handelstage das 52-Wochen-Tief zurückliegt. */
    tief_alter_bars: number | null;
  };
}

/**
 * Marktbezug für die relative Stärke. Optional: Ohne ihn bleibt der
 * Squeeze-Auslöser stumm, statt still eine Bedingung wegzulassen.
 */
export interface SetupKontext {
  /** Benchmark-Bars (SPY), aufsteigend sortiert. Wird über das Datum verknüpft. */
  benchmark?: OhlcvBar[];
  /**
   * Bewertungstag als `YYYY-MM-DD` — wogegen das Datenalter gemessen wird.
   *
   * FEHLT er, gilt die letzte Bar als aktuell (Alter 0). Das ist für die
   * Messskripte richtig, die Vergangenheit nachspielen: Dort IST die letzte
   * Bar der Bewertungstag. Produktion (Cron, Watchlist, Symbolseite) übergibt
   * ihn — sonst kann die Altersprüfung nichts blockieren.
   */
  heute?: string;
}

function sma(werte: number[], n: number): number | null {
  if (werte.length < n) return null;
  let s = 0;
  for (let i = werte.length - n; i < werte.length; i++) s += werte[i]!;
  return s / n;
}

/**
 * ATR nach WILDER (rekursive Glättung), nicht als einfacher Mittelwert.
 *
 * Das ist kein Detail: Die erste Fassung nutzte den einfachen Mittelwert über
 * n Bars und verfehlte damit die gemessenen Werte deutlich (51,3 % statt 59 %
 * Treffer). Wilder gewichtet ältere Bars exponentiell ab und reagiert dadurch
 * anders auf Volatilitäts-Regimewechsel — und genau dieser Quotient
 * ATR14/ATR100 ist das tragende Merkmal der Regel. Die Auswertung, auf der die
 * Zahlen beruhen, rechnet Wilder; also muss die Produktion es auch tun, sonst
 * misst man etwas anderes, als man ausgewiesen hat.
 */
function atr(bars: OhlcvBar[], n: number): number | null {
  if (bars.length < n + 1) return null;
  const tr: number[] = [];
  for (let i = 1; i < bars.length; i++) {
    const b = bars[i]!;
    const vc = bars[i - 1]!.close;
    tr.push(Math.max(b.high - b.low, Math.abs(b.high - vc), Math.abs(b.low - vc)));
  }
  if (tr.length < n) return null;
  // Start: einfacher Mittelwert der ersten n True Ranges, danach rekursiv.
  let wert = tr.slice(0, n).reduce((a, b) => a + b, 0) / n;
  for (let i = n; i < tr.length; i++) {
    wert = (wert * (n - 1) + tr[i]!) / n;
  }
  return wert;
}

/** Bollinger-Bandbreite in Prozent: 4 × Standardabweichung / Mittelwert. */
function bandbreite(closes: number[], bis: number): number | null {
  if (bis < 19) return null;
  const f = closes.slice(bis - 19, bis + 1);
  const m = f.reduce((a, b) => a + b, 0) / 20;
  if (!(m > 0)) return null;
  const v = f.reduce((a, b) => a + (b - m) * (b - m), 0) / 20; // Populations-Streuung
  return (4 * Math.sqrt(v)) / m * 100;
}

/**
 * Squeeze = heutige Bandbreite / Median der Bandbreite des letzten Jahres.
 * Braucht 252 + 19 = 271 Bars. Darunter gibt es `null` statt eines geratenen
 * Werts — der Auslöser bleibt dann einfach stumm.
 */
function squeezeQuotient(closes: number[]): number | null {
  const n = closes.length;
  if (n < 271) return null;
  const heute = bandbreite(closes, n - 1);
  if (heute === null || !(heute > 0)) return null;
  const reihe: number[] = [];
  for (let i = n - 252; i < n; i++) {
    const b = bandbreite(closes, i);
    if (b !== null && Number.isFinite(b)) reihe.push(b);
  }
  if (reihe.length < 200) return null;
  reihe.sort((a, b) => a - b);
  const mid = reihe.length >> 1;
  const median = reihe.length % 2 ? reihe[mid]! : (reihe[mid - 1]! + reihe[mid]!) / 2;
  if (!(median > 0)) return null;
  return heute / median;
}

/**
 * 20-Tage-Rendite des Werts minus 20-Tage-Rendite des Marktes, in
 * Prozentpunkten. Verknüpfung über das DATUM, nicht über den Index — sonst
 * verschiebt ein einzelner fehlender Handelstag im Benchmark das ganze Fenster.
 */
function rs20(bars: OhlcvBar[], benchmark: OhlcvBar[] | undefined): number | null {
  if (!benchmark || benchmark.length < 21) return null;
  const closes = bars.map((b) => b.close);
  if (closes.length < 21) return null;
  const heute = bars[bars.length - 1]!.timestamp;
  let bi = -1;
  for (let i = benchmark.length - 1; i >= 0; i--) {
    if (benchmark[i]!.timestamp <= heute) {
      bi = i;
      break;
    }
  }
  if (bi < 20) return null;
  const mNeu = benchmark[bi]!.close;
  const mAlt = benchmark[bi - 20]!.close;
  const wNeu = closes[closes.length - 1]!;
  const wAlt = closes[closes.length - 21]!;
  if (!(mAlt > 0) || !(wAlt > 0)) return null;
  return ((wNeu / wAlt - 1) - (mNeu / mAlt - 1)) * 100;
}

const LEERE_KENNZAHLEN = {
  dd52_pct: null,
  atr_quotient: null,
  ueber_ma50: null,
  roc5_pct: null,
  atr14: null,
  squeeze: null,
  rs20_pp: null,
  tief_alter_bars: null,
} as const;

/**
 * Toleranz, mit der ein Tagesverhältnis noch als glatter Split-Bruch gilt.
 * 6 % lässt Raum für die echte Tagesbewegung am Split-Tag (CRWD bewegte sich
 * zusätzlich +2,7 %) und hält gleichzeitig Abstand zu einem echten Sturz:
 * −55 % (Verhältnis 0,45) liegt 10 % neben 1/2 und wird bewertet, nicht
 * verworfen.
 */
const SPLIT_TOLERANZ = 0.06;

/**
 * Liest ein Tagesverhältnis als Split, wenn es auf einem glatten Bruch liegt.
 * Gibt die erkannte Split-Notation zurück („1:4", „10:1") oder null.
 */
export function alsSplitLesbar(verhaeltnis: number): string | null {
  if (!Number.isFinite(verhaeltnis) || verhaeltnis <= 0) return null;
  for (let n = 2; n <= 20; n++) {
    // Split n:1 — der Kurs fällt auf 1/n.
    if (Math.abs(verhaeltnis - 1 / n) / (1 / n) <= SPLIT_TOLERANZ) return `1:${n}`;
    // Zusammenlegung 1:n — der Kurs steigt auf das n-Fache.
    if (Math.abs(verhaeltnis - n) / n <= SPLIT_TOLERANZ) return `${n}:1`;
  }
  return null;
}

/**
 * Platzhalter für die Meta-Felder. Die innere Bewertung kennt weder den
 * Bewertungstag noch das Datenalter — beides setzt der Wrapper unten. Als
 * Konstante, damit keine Rückgabestelle sie vergessen kann (der Typechecker
 * meckert dann).
 */
const ROH_META = {
  as_of: null as string | null,
  datenalter_tage: null as number | null,
  tradeable: false,
  blockiert_durch: [] as Blockiergrund[],
};

function leererState(grund: string): SetupState {
  return {
    zustand: 'daten_unzureichend',
    text: `Datenlage unzureichend — ${grund}`,
    ausloeser: null,
    limit_bis: null,
    ungueltig_unter: null,
    trigger_ab: null,
    tag_von: null,
    gruende: [grund],
    as_of: null,
    datenalter_tage: null,
    tradeable: false,
    blockiert_durch: ['daten_unvollstaendig'],
    kennzahlen: { ...LEERE_KENNZAHLEN },
  };
}

/**
 * Bewertet den Zustand eines Werts allein aus seinen Kursen.
 *
 * @param bars     Daily-Bars, AUFSTEIGEND sortiert, letzter = Bewertungstag.
 *                 Für ein Urteil werden mindestens 252 Bars (1 Jahr) gebraucht;
 *                 darunter gibt es ehrlich `daten_unzureichend` statt eines
 *                 geratenen Zustands. (Score-Audit V1: kein fail-quiet.)
 * @param kontext  Optionaler Marktbezug. Fehlt er, bleibt der Squeeze-Auslöser
 *                 stumm — die Zone öffnet dann über den Wende- oder den
 *                 Stufe-2-Auslöser, die beide ohne Benchmark auskommen.
 */
function bewerteZustand(bars: OhlcvBar[], kontext?: SetupKontext): SetupState {
  if (!Array.isArray(bars) || bars.length < 252) {
    return leererState(`nur ${bars?.length ?? 0} Bars, mindestens 252 nötig`);
  }
  // Fenster-Invarianz (P0, 11.08.2026): mehr als SETUP_BARS ändern nichts.
  if (bars.length > SETUP_BARS) bars = bars.slice(-SETUP_BARS);

  const closes = bars.map((b) => b.close);
  const kurs = closes[closes.length - 1]!;
  if (!Number.isFinite(kurs) || kurs <= 0) return leererState('kein gültiger Kurs');

  const fenster52 = bars.slice(-252);

  /**
   * DATEN-PLAUSIBILITÄT (Audit 08.08.2026) — vor jeder Bewertung.
   *
   * Befund: `v5_ohlcv_history` enthält für CRWD einen UNBEREINIGTEN 4:1-Split
   * am 30.06.2026 (Schluss 742,91 → 190,78, Faktor 0,257). Daraus errechnete
   * die Zustandsmaschine einen Drawdown von −73,6 % — frei erfunden — und
   * einen ATR-Quotienten von 0,77, also FÄLSCHLICH unter der Kontraktions-
   * schwelle, weil der ATR100 die Sprungstelle noch mitträgt. Der Wert wurde
   * nur zufällig durch das V-Rebound-Verbot gerettet, nicht durch eine Prüfung.
   *
   * Das ist genau das Muster, das dieses Modul eigentlich ausschließen soll:
   * schlechte Daten erzeugen eine selbstbewusste Antwort statt „unbekannt".
   *
   * WARUM NICHT EINFACH „jeder Tagessprung über 45 %": Ein echter Kurssturz
   * von −50 % an einem Tag ist selten, aber real (Bilanzschock, gekippte
   * Studie) — und für einen antizyklischen Ansatz ist genau so ein Wert
   * später ein Kandidat. Eine grobe Schwelle würde ihn dauerhaft stumm
   * schalten. Unterschieden wird deshalb am Verhältnis selbst: Splits landen
   * per Konstruktion auf glatten Brüchen (1/2, 1/3, 1/4, 1/10) bzw. deren
   * Kehrwerten bei Zusammenlegungen. CRWD lag mit 0,257 nur 2,7 % neben 1/4;
   * ein Bilanzschock trifft solche Zahlen nicht systematisch.
   */
  for (let i = 1; i < fenster52.length; i++) {
    const vorher = fenster52[i - 1]!.close;
    const jetzt = fenster52[i]!.close;
    if (!(vorher > 0) || !(jetzt > 0)) continue;
    const split = alsSplitLesbar(jetzt / vorher);
    if (split) {
      return leererState(
        `Kurssprung am ${fenster52[i]!.timestamp} auf das ${(jetzt / vorher).toFixed(3)}-Fache — ` +
          `das ist auf ${split} genau ein unbereinigter Split in den Kursdaten. ` +
          'Ohne saubere Historie wären Drawdown und ATR frei erfunden.'
      );
    }
  }

  const hoch52 = Math.max(...fenster52.map((b) => b.high));
  const dd52 = (kurs / hoch52 - 1) * 100;

  // Alter des 52-Wochen-Tiefs in Handelstagen. Als Kennzahl geführt, nicht als
  // Verbot: „Zeit seit dem Tief" ist als EINZELMERKMAL nachweislich wirkungslos,
  // und die Wechselwirkung mit der frischen MA50-Rückeroberung ist nicht
  // gemessen — eine Sperre könnte den Wende-Auslöser gerade abschneiden.
  let tiefIdx = 0;
  for (let i = 1; i < fenster52.length; i++) {
    if (fenster52[i]!.low < fenster52[tiefIdx]!.low) tiefIdx = i;
  }
  const tiefAlter = fenster52.length - 1 - tiefIdx;

  const ma50 = sma(closes, 50);
  /**
   * War der Kurs im Reclaim-Fenster noch unter seiner damaligen MA50?
   * Wichtig: gegen die MA50 VON DAMALS prüfen, nicht gegen die heutige —
   * sonst misst man die Verschiebung des Durchschnitts statt der Kursbewegung.
   */
  const warUnterMa50 = (() => {
    for (let zurueck = 1; zurueck <= RECLAIM_FENSTER_BARS; zurueck++) {
      const bisDahin = closes.slice(0, closes.length - zurueck);
      const maDamals = sma(bisDahin, 50);
      if (maDamals === null) break;
      if (bisDahin[bisDahin.length - 1]! <= maDamals) return true;
    }
    return false;
  })();
  const atr14 = atr(bars, 14);
  const atr100 = atr(bars, 100);
  const atrQuotient = atr14 !== null && atr100 !== null && atr100 > 0 ? atr14 / atr100 : null;
  const roc5 = closes.length > 5 ? (kurs / closes[closes.length - 6]! - 1) * 100 : null;
  const squeeze = squeezeQuotient(closes);
  const rs = rs20(bars, kontext?.benchmark);

  const kennzahlen = {
    dd52_pct: Math.round(dd52 * 10) / 10,
    atr_quotient: atrQuotient !== null ? Math.round(atrQuotient * 100) / 100 : null,
    ueber_ma50: ma50 !== null ? kurs > ma50 : null,
    roc5_pct: roc5 !== null ? Math.round(roc5 * 10) / 10 : null,
    atr14: atr14 !== null ? Math.round(atr14 * 10000) / 10000 : null,
    squeeze: squeeze !== null ? Math.round(squeeze * 1000) / 1000 : null,
    rs20_pp: rs !== null ? Math.round(rs * 10) / 10 : null,
    tief_alter_bars: tiefAlter,
  };

  const basis = { ausloeser: null, limit_bis: null, trigger_ab: null, tag_von: null, ...ROH_META, kennzahlen };

  /**
   * Der Stop hängt am EINSTIEGSPREIS, nicht am heutigen Schluss.
   *
   * Befund Audit 08.08.2026: Vorher stand hier `kurs − 4,5 × ATR14`, während
   * gekauft wird zum `limit_bis` (beim Wende-Auslöser `max(kurs, MA50 × 1,02)`,
   * also regelmäßig ÜBER dem Schluss). Der tatsächliche Abstand zwischen
   * Kaufpreis und Stop betrug damit über die handelbaren Werte im Schnitt
   * 4,97 × ATR und im Extremfall 6,91 × ATR — bis zu 54 % mehr Risiko, als die
   * Konstante behauptet, und das R, in dem alle Ergebnisse gemessen sind,
   * stimmte nicht mit dem gehandelten R überein.
   */
  const stopFuer = (einstieg: number): number | null =>
    atr14 !== null ? einstieg - atr14 * STOP_ATR14_FAKTOR : null;

  // ---- Ebene 1: Verbote (nicht kompensierbar) ----
  // Bewusst ZUERST und als harte Exits: Ein Verbot darf nicht durch gute Werte
  // an anderer Stelle weggerechnet werden (OECD/JRC: nicht-kompensatorisch).
  if (dd52 > VERBOT_NAHE_HOCH_PCT) {
    return {
      ...basis,
      zustand: 'kein_setup',
      text: `Kein Setup · ${Math.abs(dd52).toFixed(0)} % unter Hoch — zu nah dran, nichts zu tun`,
      ungueltig_unter: null,
      gruende: [`nur ${Math.abs(dd52).toFixed(1)} % unter dem 52W-Hoch (Mindestabstand 5 %)`],
    };
  }
  // Das V-Rebound-Verbot stand hier bis zum 08.08.2026. Siehe die Begründung
  // an ROC5_HINWEIS_PCT: gemessen wirkungslos, blockierte aber den
  // MA200-Durchbruch von PYPL am 15.07.2026.

  // ---- Ebene 2: Ist es überhaupt ein antizyklischer Kandidat? ----
  if (dd52 > KANDIDAT_DD_MIN_PCT) {
    return {
      ...basis,
      zustand: 'kein_setup',
      text: `Kein Setup · ${Math.abs(dd52).toFixed(0)} % unter Hoch — noch kein Rücksetzer`,
      ungueltig_unter: null,
      gruende: [`Rücksetzer erst ab ${Math.abs(KANDIDAT_DD_MIN_PCT)} % (aktuell ${Math.abs(dd52).toFixed(1)} %)`],
    };
  }
  // BEHARRLICHKEIT statt Schwellenzittern (Audit 08.08.2026).
  //
  // Befund an PYPL: Zwischen 2025-01 und 2026-08 wechselte der Zustand 28-mal
  // zwischen `boden_reift` und `kein_setup` — nicht weil sich etwas geändert
  // hätte, sondern weil dd52 um die −40-%-Marke pendelte (−39,8 → −40,6 →
  // −39,9 → −41,2). Eine harte Schwelle auf einer verrauschten Grösse erzeugt
  // Fehlalarme in beide Richtungen und macht jede Benachrichtigung wertlos.
  //
  // Die Grenze gilt deshalb erst, wenn sie DREI Tage in Folge verletzt ist.
  // Das 52-Wochen-Hoch wandert langsam genug, um es für diese drei Tage als
  // konstant anzunehmen.
  const dauerhaftUnter = (grenze: number): boolean => {
    for (let k = 0; k < 3; k++) {
      const c = closes[closes.length - 1 - k];
      if (c === undefined) return false;
      if ((c / hoch52 - 1) * 100 >= grenze) return false;
    }
    return true;
  };
  /**
   * TIEFE ZONE — vom Verbot zur Warnung (08.08.2026).
   *
   * Bis heute war „tiefer als −40 %" ein harter Ausschluss: `kein_setup`,
   * kein Trigger-Preis, kein Wort mehr dazu. Der Realfall, der das gekippt
   * hat, ist PYPL: Vom 02.02. bis 08.04.2026 — die gesamte Bodenbildung, Tief
   * 38,22 am 12.02. — meldete die App an JEDEM Tag „zu tief gefallen, kein
   * Boden erkennbar". Der Wert steht heute bei 59,78.
   *
   * Die Messung sagt dasselbe. Auf 19.478 Einstiegen ist die ausgeschlossene
   * Gruppe die BESTE des ganzen Datensatzes:
   *   Erwartung  +0,229 R  95 %-Bereich [+0,065 , +0,458]
   *   Gewinner   +7,1 pp   95 %-Bereich [+1,9 , +10,2]
   *
   * ABER: Genau dort ist die Überlebensauswahl maximal. Das Universum ist die
   * heutige Watchlist — jeder Wert darin hat überlebt, Pleiten fehlen. Diese
   * Zahl ist mit Sicherheit zu optimistisch, und wie stark, lässt sich ohne
   * Daten zu delisteten Werten nicht sagen.
   *
   * Warum die Grenze trotzdem fällt: Der Verlust ist durch den festen Stop bei
   * −1R gedeckelt. Die Verzerrung bläht die GEWINNER auf, nicht die Verluste —
   * ein Wert, der pleitegegangen wäre, hätte wie jeder andere Verlierer bei
   * −1R geschlossen (Gap-Ehrlichkeit inklusive: fällt die Eröffnung tiefer,
   * wird dort gefüllt).
   *
   * Was BLEIBT: Die tiefe Zone öffnet keine Kaufzone von allein. Sie braucht
   * wie jede andere einen Auslöser. Nur schweigt die App nicht mehr — sie
   * zeigt den Trigger-Preis und sagt dazu, dass die Datenlage hier am
   * dünnsten ist.
   */
  const tiefeZone = dd52 < KANDIDAT_DD_MAX_PCT && dauerhaftUnter(KANDIDAT_DD_MAX_PCT);

  // ---- Ebene 3: Hat einer der Auslöser gefeuert? ----
  if (ma50 === null || atrQuotient === null) {
    return leererState('MA50 oder ATR nicht berechenbar');
  }

  const ueberMa50 = kurs > ma50;
  // Auslöser 1 — Wende: über MA50 UND innerhalb des Fensters noch darunter.
  const kontraktion = atrQuotient <= KONTRAKTION_MAX;
  const wende = ueberMa50 && warUnterMa50 && kontraktion;
  // Auslöser 2 — Squeeze: Bänder zusammengelaufen, nicht schwächer als der
  // Markt, und der Boden hat Zeit gehabt.
  const squeezeEng = squeeze !== null && squeeze < SQUEEZE_MAX;
  const rsOk = rs !== null && rs >= RS20_MIN_PP;
  const tiefReif = tiefAlter >= SQUEEZE_TIEF_MIN_ALTER_BARS;
  const squeezeSetup = squeezeEng && rsOk && tiefReif;

  /**
   * Auslöser 3 — STUFE 2 (neu am 08.08.2026).
   *
   * Der Übergang von Weinsteins Phase 1 (Bodenbildung) in Phase 2 (Aufwärts):
   * Der Kurs durchbricht die Langfristlinie von unten, nachdem er lange
   * darunter lag, und die Linie fällt nicht mehr.
   *
   * KORREKTUR 08.08.2026, ABENDS — die ursprüngliche Begründung war falsch
   * belegt und ist inzwischen widerlegt worden. Sie bleibt hier stehen, damit
   * der Fehler nachvollziehbar ist statt spurlos zu verschwinden:
   *
   * Behauptet wurde „MA200-Durchbruch 770 Signale, 70,1 % Trefferlage,
   * +11,8 pp Vorsprung", mit Quellenangabe `scripts/messung-phasen.ts`.
   * Dieses Skript enthält kein einziges Vorkommen von MA200. Die Zahlen
   * stammten aus einem Wegwerfskript, das nie ins Repo kam — also aus einer
   * Messung, die niemand nachrechnen konnte. Genau das Muster, das dieses
   * Modul ausschliessen soll.
   *
   * WAS DIE NACHPRÜFBARE MESSUNG SAGT (scripts/messung-kandidaten.ts,
   * vorregistriert, Entwicklung bis 2015 und EINE Auswertung auf dem
   * gesperrten Zeitraum 2016–2026):
   *
   *   Erwartung gegen die Nulllinie „jeden 20. Tag blind kaufen", in R:
   *                        Entwicklung           Sperrzeitraum
   *   wende          −0,062 [−0,293, +0,179]   −0,002 [−0,425, +0,447]
   *   squeeze        +0,083 [−0,366, +0,559]   +0,423 [−0,039, +0,922]
   *   stufe2         −0,432 [−0,655, −0,190]   +0,164 [−0,203, +0,502]
   *
   * KEIN Auslöser nimmt die Hürde — in keinem der beiden Zeiträume enthält
   * ein Bereich nicht die Null. Alle echten Einstiege zusammen gegen die
   * Nulllinie: −0,010 R [−0,206, +0,179]. Die gesamte Filterarbeit liefert
   * exakt das Ergebnis von blindem Kaufen.
   *
   * Der ursprüngliche Beleg für `stufe2` — die Trefferlage in einer
   * Aufwärtsphase — ist nicht falsch gerechnet, misst aber die falsche
   * Sache: Häufiger in einer Aufwärtsphase zu landen übersetzt sich NICHT
   * in Ertrag. Im Entwicklungszeitraum war stufe2 sogar signifikant
   * schlechter als blindes Kaufen; im Sperrzeitraum drehte das ins Positive,
   * ohne die Hürde zu nehmen. Aus „widerlegt" wurde „ebenfalls nicht
   * nachgewiesen" — nicht „bestätigt".
   *
   * WARUM ER TROTZDEM BLEIBT: Er ist nicht schlechter als die beiden
   * anderen, und alle drei erfüllen denselben Zweck — sie erzeugen Seltenheit
   * und eine feste Regel. Das ist Disziplin, kein gemessener Vorsprung, und
   * so muss es auch auf dem Bildschirm stehen. Wer diesen Auslöser als
   * „gemessen" oder „geprüft" bezeichnet, wiederholt den Fehler oben.
   *
   * Die Bedingung `ma200 >= ma200Vor20 * 0.98` ist Weinsteins „die Linie
   * verflacht": Ein Durchbruch gegen eine steil fallende Linie ist eine
   * Zwischenerholung in Phase 4, kein Phasenwechsel.
   */
  const ma200 = sma(closes, 200);
  const ma200Vor20 = closes.length >= 220 ? sma(closes.slice(0, -20), 200) : null;
  let langUnterMa200 = false;
  if (ma200 !== null && closes.length >= 221) {
    langUnterMa200 = true;
    for (let k = 1; k <= STUFE2_VORLAUF_BARS; k++) {
      const bis = closes.length - k;
      const mk = sma(closes.slice(0, bis), 200);
      if (mk === null || closes[bis - 1]! > mk) { langUnterMa200 = false; break; }
    }
  }
  const stufe2 =
    ma200 !== null &&
    ma200Vor20 !== null &&
    kurs > ma200 &&
    langUnterMa200 &&
    ma200 >= ma200Vor20 * 0.98;

  if (wende || squeezeSetup || stufe2) {
    // Mehrere Auslöser gleichzeitig werden benannt, nicht verrechnet — der
    // Nutzer soll sehen, WAS gefeuert hat, nicht eine Summe davon.
    const teile: string[] = [];
    if (wende) teile.push('wende');
    if (squeezeSetup) teile.push('squeeze');
    if (stufe2) teile.push('stufe2');
    const ausloeser = teile.join('+') as SetupAusloeser;
    /**
     * Obergrenze der Kaufzone. Beim Wende-Auslöser liegt sie knapp über der
     * zurückeroberten MA50 — darüber kauft man der Bewegung hinterher, was die
     * Messung bestraft. Der Squeeze-Auslöser hat keine solche Linie; dort ist
     * die Enge selbst die Begründung, also gilt der heutige Kurs plus die
     * halbe Tagesschwankung als Limit.
     */
    // Beim Stufe-2-Durchbruch ist die MA200 die Linie, an der gekauft wird:
    // Ein Rücklauf auf sie ist der klassische Nachkauf, darüber hinaus läuft
    // man der Bewegung hinterher. Beim Wende-Auslöser gilt dasselbe für die
    // MA50. Der Squeeze hat keine solche Linie — dort der heutige Kurs plus
    // die halbe Tagesschwankung.
    const limit = stufe2
      ? Math.max(kurs, ma200! * 1.02)
      : wende
        ? Math.max(kurs, ma50 * 1.02)
        : kurs + (atr14 ?? 0) * 0.5;
    const stop = stopFuer(limit);
    const gruende: string[] = [`${Math.abs(dd52).toFixed(0)} % unter dem Hoch`];
    if (tiefeZone) gruende.push(TIEFE_ZONE_WARNUNG);
    if (wende) {
      gruende.push(
        `MA50 frisch zurückerobert (war in den letzten ${RECLAIM_FENSTER_BARS} Tagen noch darunter)`,
        `Volatilität eingetrocknet (ATR-Quotient ${atrQuotient.toFixed(2)})`
      );
    }
    if (squeezeSetup) {
      gruende.push(
        `Bollinger-Bänder auf ${(squeeze! * 100).toFixed(0)} % ihrer Jahres-Normalbreite zusammengelaufen`,
        `20 Tage ${rs! >= 0 ? '+' : ''}${rs!.toFixed(1)} Prozentpunkte gegen den Markt`,
        `Tief liegt ${tiefAlter} Handelstage zurück — der Boden hatte Zeit`
      );
    }
    if (stufe2) {
      gruende.push(
        `MA200 (${ma200!.toFixed(2)}) von unten durchbrochen — lag ${STUFE2_VORLAUF_BARS} Tage darunter`,
        'die MA200 fällt nicht mehr — Übergang von Bodenbildung in Aufwärtsphase'
      );
      if (roc5 !== null && roc5 >= ROC5_HINWEIS_PCT) {
        gruende.push(
          `Hinweis: +${roc5.toFixed(1)} % in 5 Tagen — der Durchbruch kam schnell, das Limit hält den Einstieg nah an der Linie`
        );
      }
    }
    return {
      ...ROH_META,
      zustand: 'zone_offen',
      ausloeser,
      text:
        `Kaufzone offen · Limit bis ${limit.toFixed(2)}` +
        (stop !== null ? ` · ungültig unter ${stop.toFixed(2)}` : ''),
      limit_bis: Math.round(limit * 100) / 100,
      ungueltig_unter: stop !== null ? Math.round(stop * 100) / 100 : null,
      trigger_ab: null,
      tag_von: 1,
      gruende,
      kennzahlen,
    };
  }

  // Kandidat, aber kein Auslöser → beobachten, mit Trigger-PREIS.
  const fehlt: string[] = [];
  if (!ueberMa50) fehlt.push(`Kurs noch unter MA50 (${ma50.toFixed(2)})`);
  else if (!warUnterMa50)
    fehlt.push(
      `läuft seit über ${RECLAIM_FENSTER_BARS} Tagen über der MA50 — keine frische Wende, sondern ein laufender Trend`
    );
  if (!kontraktion)
    fehlt.push(`Volatilität noch nicht eingetrocknet (ATR-Quotient ${atrQuotient.toFixed(2)}, nötig ≤ ${KONTRAKTION_MAX})`);
  if (squeeze === null) fehlt.push('Bollinger-Squeeze nicht berechenbar (weniger als 271 Bars)');
  else if (!squeezeEng)
    fehlt.push(`Bänder noch weit (${(squeeze * 100).toFixed(0)} % der Jahres-Normalbreite, nötig < ${SQUEEZE_MAX * 100} %)`);
  else if (rs === null) fehlt.push('kein Marktvergleich verfügbar — Squeeze allein reicht nicht');
  else if (!rsOk) fehlt.push(`20 Tage ${rs.toFixed(1)} Prozentpunkte schwächer als der Markt`);
  else if (!tiefReif)
    fehlt.push(
      `Tief erst ${tiefAlter} Handelstage her — ein Boden mit Squeeze braucht mindestens ${SQUEEZE_TIEF_MIN_ALTER_BARS}`
    );

  // Auch hier hängt der Stop am künftigen Einstieg (der Kaufzone über der
  // MA50), nicht am heutigen Schluss — sonst zeigt die Vorschau ein Risiko an,
  // das beim späteren Kauf gar nicht gilt.
  const stop = stopFuer(ma50 * 1.02);

  if (tiefeZone) fehlt.unshift(TIEFE_ZONE_WARNUNG);

  return {
    zustand: 'boden_reift',
    ausloeser: null,
    text:
      (tiefeZone ? 'Tiefe Zone · ' : 'Boden reift · ') +
      `Kaufzone öffnet über ${ma50.toFixed(2)}` + (kontraktion ? '' : ' (wenn die Schwankung nachlässt)'),
    limit_bis: null,
    ungueltig_unter: stop !== null ? Math.round(stop * 100) / 100 : null,
    trigger_ab: Math.round(ma50 * 100) / 100,
    tag_von: null,
    gruende: fehlt,
    ...ROH_META,
    kennzahlen,
  };
}


/**
 * Sucht in einer Kursreihe nach unbereinigten Splits.
 *
 * Ausgelagert am 10.08.2026, weil die Zustandsmaschine diesen Riegel hatte
 * und die beiden Backtest-Engines nicht (Audit-Befund). Ein unbereinigter
 * Split erzeugt dort einen erfundenen Kurssturz — und damit einen Stop-Out,
 * den es nie gab, mitten in einer Statistik, die anschliessend Entscheidungen
 * begruendet.
 *
 * Gibt lesbare Fundstellen zurueck; eine leere Liste heisst „nichts
 * Auffaelliges", nicht „garantiert sauber".
 */
export function findeSplitVerdacht(
  bars: Array<{ timestamp: string; close: number }>
): string[] {
  const funde: string[] = [];
  for (let i = 1; i < bars.length; i++) {
    const vorher = bars[i - 1]!.close;
    const jetzt = bars[i]!.close;
    if (!(vorher > 0) || !(jetzt > 0)) continue;
    const notation = alsSplitLesbar(jetzt / vorher);
    if (notation) {
      funde.push(
        `${bars[i]!.timestamp}: ${vorher.toFixed(2)} → ${jetzt.toFixed(2)} `
          + `sieht aus wie ein unbereinigter Split (${notation}).`
      );
    }
  }
  return funde;
}

/** Kalendertage zwischen zwei ISO-Tagen (`YYYY-MM-DD`). null bei Unsinn. */
function tageZwischen(von: string, bis: string): number | null {
  const a = Date.parse(`${von}T00:00:00Z`);
  const b = Date.parse(`${bis}T00:00:00Z`);
  if (!Number.isFinite(a) || !Number.isFinite(b)) return null;
  return Math.round((b - a) / 86_400_000);
}

/**
 * Bewertet den Zustand eines Werts allein aus seinen Kursen — und entscheidet
 * zusätzlich, ob daraus GEHANDELT werden darf.
 *
 * Die Trennung ist der Kern von P0-3 (Audit 11.08.2026): Der Zustand
 * beschreibt den Wert, `tradeable` beschreibt, ob wir ihm gerade trauen. Ein
 * `zone_offen` auf drei Wochen alten Bars bleibt sichtbar — es löst nur
 * nichts mehr aus, und der Grund steht in `blockiert_durch`.
 *
 * Nur dieses Feld darf einen Kauf-Alert oder einen übernehmbaren Plan
 * auslösen. Kein Score, keine KI, keine Oberfläche darf es überschreiben.
 */
export function computeSetupState(bars: OhlcvBar[], kontext?: SetupKontext): SetupState {
  const roh = bewerteZustand(bars, kontext);

  const letzte = Array.isArray(bars) && bars.length > 0
    ? (bars[bars.length - 1]!.timestamp ?? '').slice(0, 10)
    : null;
  const as_of = letzte && letzte.length === 10 ? letzte : null;

  // Ohne übergebenen Bewertungstag gilt die letzte Bar als aktuell — richtig
  // für die Messskripte, die Vergangenheit nachspielen.
  const datenalter_tage =
    as_of === null ? null : kontext?.heute ? tageZwischen(as_of, kontext.heute) : 0;

  const blockiert_durch: Blockiergrund[] = [];
  if (roh.zustand === 'daten_unzureichend') blockiert_durch.push('daten_unvollstaendig');
  if (datenalter_tage !== null && datenalter_tage > MAX_DATENALTER_TAGE) {
    blockiert_durch.push('daten_veraltet');
  }

  const tradeable = roh.zustand === 'zone_offen' && blockiert_durch.length === 0;

  // Der Grund gehört auch in den Klartext — wer die Karte liest, soll nicht
  // erst ein Feld aufklappen müssen, um zu verstehen, warum nichts passiert.
  const gruende = blockiert_durch.includes('daten_veraltet')
    ? [
        `Kursdaten sind ${datenalter_tage} Tage alt (Stand ${as_of}) — nicht handelbar, ` +
          'bis frische Bars da sind.',
        ...roh.gruende,
      ]
    : roh.gruende;

  return { ...roh, as_of, datenalter_tage, tradeable, blockiert_durch, gruende };
}
